/**
 * Was eine Lizenz einem Projekt oder einem Dataspace nimmt und was man dagegen tun kann: die Seite,
 * die an die Stelle eines geschlossenen Designers oder eines geschlossenen Dataspace tritt, der
 * Hinweis auf einem schreibgeschützten, die Marken der Listen und die Texte der Ablehnungen.
 *
 * Die Schlüssel unter `note`, `short` und `errors` sind nach den Gründen und Codes des Servers
 * benannt (`access::restriction`) und werden dynamisch nachgeschlagen (`utils/spaceRestriction.ts`);
 * diese Namen sind also ein Vertrag mit der API und nicht frei wählbar. `other` sagt es für einen
 * Grund, für den dieser Client noch keinen Text hat. Eine Variante auf `Named` oder `Counted` gilt,
 * wenn die Abteilung oder die Zahlen bekannt sind.
 */
export default {
	spaceRestriction: {
		title: {
			designer: "Der Designer steht für dieses Projekt nicht zur Verfügung",
			project: "Dieses Projekt ist schreibgeschützt",
			dataspace: "Prozesse können nicht geöffnet oder gestartet werden",
		},
		note: {
			designer: {
				server: "Die Lizenz dieses Servers enthält den Designer nicht, deshalb kann hier kein Projekt geöffnet oder bearbeitet werden. Das kann nur ein Serveradministrator ändern, in den Lizenzeinstellungen des Kontrollzentrums.",
				department:
					"Die Lizenz der zugehörigen Abteilung enthält den Designer nicht. Ein Abteilungseigentümer kann LiveReader bitten, ihn hinzuzufügen, oder das Projekt kann in eine andere Abteilung verschoben werden.",
				departmentNamed:
					"Die Lizenz von {department} enthält den Designer nicht. Ein Abteilungseigentümer kann LiveReader bitten, ihn hinzuzufügen, oder das Projekt kann in eine andere Abteilung verschoben werden.",
				other: "Der Designer steht für dieses Projekt nicht zur Verfügung.",
			},
			locked: {
				department_unlicensed:
					"Die Lizenz der Abteilung ist nicht gültig. Ein Abteilungseigentümer kann ihren Schlüssel erneuern.",
				department_unlicensedNamed:
					"Die Lizenz von {department} ist nicht gültig. Ein Abteilungseigentümer kann ihren Schlüssel erneuern.",
				personal_spaces_disabled:
					"Die Lizenz dieses Servers enthält keine persönlichen Spaces. Verschieben Sie diesen Space in eine Abteilung.",
				personal_member_limit:
					"Dieser persönliche Space hat mehr Mitglieder, als die Lizenz erlaubt. Entfernen Sie Mitglieder oder verschieben Sie ihn in eine Abteilung.",
				personal_member_limitCounted:
					"Dieser persönliche Space hat mehr Mitglieder, als die Lizenz erlaubt ({members} von {limit}). Entfernen Sie Mitglieder oder verschieben Sie ihn in eine Abteilung.",
				other: "Dieser Space ist gesperrt, bis seine Lizenz in Ordnung ist.",
			},
		},
		running:
			"Prozesse, die bereits laufen, laufen weiter, und Sie können sie weiterhin stoppen, neu starten, archivieren oder löschen.",
		short: {
			designer: {
				server: "Die Lizenz des Servers enthält den Designer nicht.",
				department: "Die Lizenz der Abteilung enthält den Designer nicht.",
				other: "Der Designer steht nicht zur Verfügung.",
			},
			locked: {
				department_unlicensed: "Die Lizenz der Abteilung ist nicht gültig.",
				personal_spaces_disabled:
					"Die Lizenz des Servers enthält keine persönlichen Spaces.",
				personal_member_limit: "Mehr Mitglieder, als ein persönlicher Space haben darf.",
				other: "Gesperrt, bis seine Lizenz in Ordnung ist.",
			},
		},
		badge: {
			designer: "Designer geschlossen",
			project: "Schreibgeschützt",
			dataspace: "Gesperrt",
		},
		fix: {
			members: "Mitglieder verwalten",
			move: "Abteilung wählen",
			department: "{department} öffnen",
			departments: "Abteilungen öffnen",
			license: "Lizenzeinstellungen öffnen",
		},
		tryAgain: "Erneut versuchen",
		picker: {
			personalOff:
				"Die Lizenz dieses Servers enthält keine persönlichen Spaces, deshalb ist eine Abteilung der einzige Ort dafür.",
			noPlace:
				"Die Lizenz dieses Servers enthält keine persönlichen Spaces, und Sie dürfen in keiner Abteilung etwas anlegen.",
			noPersonalMove:
				"Die Lizenz dieses Servers enthält keine persönlichen Spaces, deshalb kann dieser Space nicht persönlich gemacht werden. Er kann in eine andere Abteilung verschoben werden.",
		},
		memberLimit: {
			hint: "Ein persönlicher Space darf höchstens {limit} Mitglieder haben, den Eigentümer eingeschlossen. Derzeit belegt: {taken}, offene Einladungen mitgezählt.",
		},
		errors: {
			personal_spaces_disabled:
				"Die Lizenz dieses Servers enthält keine persönlichen Spaces. Wählen Sie stattdessen eine Abteilung.",
			designer_unavailable: {
				server: "Die Lizenz dieses Servers enthält den Designer nicht, deshalb kann kein Projekt angelegt werden.",
				department:
					"Die Lizenz dieser Abteilung enthält den Designer nicht, deshalb kann darin kein Projekt angelegt werden.",
				other: "Der Designer steht dort nicht zur Verfügung.",
			},
			space_locked: {
				department_unlicensed:
					"Die Lizenz dieser Abteilung ist nicht gültig, deshalb kann darin nichts angelegt werden, bis ein Abteilungseigentümer ihren Schlüssel erneuert.",
				personal_spaces_disabled:
					"Die Lizenz dieses Servers enthält keine persönlichen Spaces, deshalb ist dieser Space gesperrt. Verschieben Sie ihn in eine Abteilung.",
				personal_member_limit:
					"Dieser persönliche Space hat mehr Mitglieder, als die Lizenz erlaubt, deshalb ist er gesperrt. Entfernen Sie Mitglieder oder verschieben Sie ihn in eine Abteilung.",
				other: "Dieser Space ist gesperrt, bis seine Lizenz in Ordnung ist.",
			},
			memberLimit: {
				limit: "Dieser persönliche Space darf gar keine Mitglieder haben. | Dieser persönliche Space darf höchstens ein Mitglied haben. | Dieser persönliche Space darf höchstens {limit} Mitglieder haben.",
				has: "Er hat keines. | Er hat ein Mitglied. | Er hat {members} Mitglieder.",
				pending: "Eine Einladung ist offen. | {pending} Einladungen sind offen.",
				addTail:
					"Entfernen Sie zuerst Mitglieder oder nehmen Sie Einladungen zurück, oder verschieben Sie den Space in eine Abteilung.",
				inviteeTail:
					"Bitten Sie die einladende Person, Platz zu schaffen oder den Space in eine Abteilung zu verschieben.",
				moveTail:
					"Entfernen Sie Mitglieder, bevor Sie ihn persönlich machen, oder lassen Sie ihn in seiner Abteilung.",
				named: "{name}: {message}",
			},
		},
	},
};
